<template>
  <v-dialog
    :model-value="modelValue"
    max-width="460"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="support-deduct-modal">
      <header class="support-deduct-modal__head">
        <h3 class="support-deduct-modal__title">{{ ui.modalTitle }}</h3>
        <button
          type="button"
          class="support-deduct-modal__close"
          aria-label="닫기"
          @click="emit('cancel')"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>

      <div class="support-deduct-modal__body">
        <p class="support-deduct-modal__desc">
          {{ ui.modalDesc }}
        </p>

        <div v-if="items.length === 0" class="support-deduct-modal__empty">
          {{ ui.emptyList }}
        </div>

        <ul v-else class="support-deduct-modal__list">
          <li
            v-for="item in items"
            :key="item.key"
            class="support-deduct-modal__row"
          >
            <label class="support-deduct-modal__label">
              <input
                type="checkbox"
                class="support-deduct-modal__check"
                :checked="localFlags[item.key]"
                @change="onToggleItem(item.key, ($event.target as HTMLInputElement).checked)"
              />
              <span class="support-deduct-modal__name">{{ item.label }}</span>
            </label>
            <span class="support-deduct-modal__amount">
              {{ formatMoney(item.amount) }}<span class="support-deduct-modal__unit">원</span>
            </span>
          </li>
        </ul>

        <section v-if="items.length > 0" class="support-deduct-modal__summary">
          <div class="support-deduct-modal__summary-row">
            <span class="support-deduct-modal__summary-label">{{ ui.targetSumLabel }}</span>
            <span class="support-deduct-modal__summary-value">
              {{ formatMoney(baseTotal) }}<span class="support-deduct-modal__unit">원</span>
            </span>
          </div>
          <div class="support-deduct-modal__summary-row support-deduct-modal__summary-row--total">
            <span class="support-deduct-modal__summary-label">{{ ui.previewLabel }}</span>
            <span class="support-deduct-modal__summary-value">
              {{ formatMoney(previewTotal) }}<span class="support-deduct-modal__unit">원</span>
            </span>
          </div>
          <div v-if="formula" class="support-deduct-modal__formula">{{ formula }}</div>
        </section>
      </div>

      <footer class="support-deduct-modal__footer">
        <v-btn variant="text" class="btn-cancel" size="small" @click="emit('cancel')">취소</v-btn>
        <v-btn variant="flat" class="btn-confirm" size="small" :disabled="items.length === 0" @click="emit('apply')">
          적용
        </v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  HOME_WAGE_SUPPORT_DEDUCTION_UI,
  type HomeWageSupportDeductionItemFlags,
  type HomeWageSupportDeductionItemKey,
  type HomeWageSupportDeductionItemRow,
} from '@/policies/homeWageSupportDeductionPolicy';

const ui = HOME_WAGE_SUPPORT_DEDUCTION_UI;

const props = defineProps<{
  modelValue: boolean;
  items: HomeWageSupportDeductionItemRow[];
  flags: HomeWageSupportDeductionItemFlags;
  baseTotal: number;
  previewTotal: number;
  formula?: string;
  formatMoney: (n: number) => string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  cancel: [];
  apply: [];
  'update:flags': [flags: HomeWageSupportDeductionItemFlags];
}>();

const localFlags = ref<HomeWageSupportDeductionItemFlags>({ ...props.flags });

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      localFlags.value = { ...props.flags };
    }
  }
);

watch(
  () => props.flags,
  (flags) => {
    if (!props.modelValue) {
      localFlags.value = { ...flags };
    }
  },
  { deep: true }
);

const onToggleItem = (key: HomeWageSupportDeductionItemKey, checked: boolean) => {
  localFlags.value = { ...localFlags.value, [key]: checked };
  emit('update:flags', { ...localFlags.value });
};

const baseTotal = computed(() => props.baseTotal);
const previewTotal = computed(() => props.previewTotal);
const formula = computed(() => props.formula || '');
</script>

<style lang="scss">
@import '@/assets/styles/variables';
</style>
